import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import Table from 'Components/DesignSystem/Table/Table';
import { PAGE_GRID_MAX_HEIGHT } from 'Components/DesignSystem/Table/pageGridHeight';
import Menu from 'Components/DesignSystem/Menu/Menu';
import Pagination from 'Components/DesignSystem/Pagination/Pagination';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';
import { TableColumn } from 'types/DesignSystem/Table';
import { MenuItem } from 'types/DesignSystem/Menu';
import { formatDateDDMMMYYYY } from 'Common/HelperClass';
import { ChangeIcon, CopyIcon, DeleteIcon, EditIcon, OverFlowIcon } from 'assests/GmsIcons';
import AuthManager from 'Common/AuthManager';

/**
 * The saved-lists grid on the DS `Table` — its first app consumer (§ 81). Sort
 * stays fully controlled and two-state: the parent's `handleSort` flips
 * ASC/DESC itself, so the DS header's own asc→desc→null cycle never applies —
 * we ignore the direction it suggests and only forward the column id.
 *
 * The row-overflow menu keeps the legacy portal-to-body positioning (the table
 * wrapper is an overflow container, which would clip an absolutely-positioned
 * popover) but the popover itself is now the DS `Menu`: real menuitems,
 * Escape/outside dismissal via `onDismiss`, and id-keyed testids replacing the
 * index-templated DOM ids specs used to have to fall back to.
 */

/** 10000 is the legacy "All" sentinel the backend page-size API expects. */
const PAGE_SIZE_OPTIONS = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
    { value: 10000, label: 'All' },
];

type ListTableProps = {
    listData: any[];
    loading: boolean;
    emptyContent?: React.ReactNode;
    handleClick: (index: number) => void;
    handleDelete: (index: number) => void;
    handleCopy: (index: number) => void;
    handleChangeOwner: (index: number) => void;
    createPermission: boolean;
    updatePermission: boolean;
    deletePermission: boolean;
    transferPermission: boolean;
    sortBy: string;
    sortOrder: 'ASC' | 'DESC';
    onSort: (column: string) => void;
    paginationParams: { page: number; per_page: number };
    totalRecords: number;
    handlePageChange: (page: number) => void;
    updatePageSize: (size: number) => void;
};

function ListTable({
    listData,
    loading,
    emptyContent,
    handleClick,
    handleDelete,
    handleCopy,
    handleChangeOwner,
    createPermission,
    updatePermission,
    deletePermission,
    transferPermission,
    sortBy,
    sortOrder,
    onSort,
    paginationParams,
    totalRecords,
    handlePageChange,
    updatePageSize,
}: ListTableProps) {
    const currentUserId = String(AuthManager.getAuthUser()?.profile?.id ?? '');
    const [selectIndex, setSelectIndex] = useState<number | null>(null);
    // `right` rather than `left`: the menu hangs leftward from the kebab, so
    // anchoring its right edge to the trigger's keeps it on-screen at any width.
    const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

    useEffect(() => {
        // A fixed-position menu detaches from its row the moment anything
        // scrolls, so scrolling closes it. Outside-click and Escape are the
        // Menu's own job (`onDismiss`).
        function handleScroll() {
            setSelectIndex(null);
        }
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, []);

    const handleOverflowClick = (event: React.MouseEvent<HTMLButtonElement>, index: number) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({ top: rect.bottom + 2, right: window.innerWidth - rect.right });
        setSelectIndex(prev => (prev === index ? null : index));
    };

    const renderRowActions = (row: any, rowIndex: number) => {
        const ownsRow = !!(currentUserId && String(row?.created_by_userid) === currentUserId);
        const noOwnerSet = !row?.created_by_userid;
        const editEnabled = ownsRow || noOwnerSet;
        const deleteEnabled = ownsRow || noOwnerSet;
        const hasAnyOption = updatePermission || createPermission || deletePermission || transferPermission;
        if (!hasAnyOption) return null;

        const rowKey = row?.id ?? rowIndex;
        const menuItems: MenuItem[] = [
            ...(updatePermission ? [{
                id: 'edit', label: 'Edit', icon: <EditIcon />, disabled: !editEnabled,
                'data-testid': `list-listtable-row-menu-edit-${rowKey}`,
                onClick: () => { setSelectIndex(null); handleClick(rowIndex); },
            }] : []),
            ...(createPermission ? [{
                id: 'copy', label: 'Copy', icon: <CopyIcon />,
                'data-testid': `list-listtable-row-menu-copy-${rowKey}`,
                onClick: () => { setSelectIndex(null); handleCopy(rowIndex); },
            }] : []),
            ...(transferPermission ? [{
                id: 'change-owner', label: 'Change Owner', icon: <ChangeIcon />,
                'data-testid': `list-listtable-row-menu-change-owner-${rowKey}`,
                onClick: () => { setSelectIndex(null); handleChangeOwner(rowIndex); },
            }] : []),
            // An owner-less row keeps its Delete item clickable even though the
            // parent's strict ownership check will refuse it — the documented
            // must-not-regress trap (okf/list/e2e.md § 7). Do not "fix" this
            // here by disabling it.
            ...(deletePermission ? [{
                id: 'delete', label: 'Delete', icon: <DeleteIcon />, disabled: !deleteEnabled,
                'data-testid': `list-listtable-row-menu-delete-${rowKey}`,
                onClick: () => { setSelectIndex(null); handleDelete(rowIndex); },
            }] : []),
        ];

        return (
            <>
                {/* § 212 — the hand-rolled p-1/hover shell becomes the DS square.
                    Same element, same handler: the menu-position math reads
                    event.currentTarget's rect, which is still this button. */}
                <IconButton
                    variant="subtle"
                    size="sm"
                    aria-label="More options"
                    aria-haspopup="menu"
                    aria-expanded={selectIndex === rowIndex}
                    data-testid={`list-listtable-row-overflow-${rowKey}`}
                    onClick={(e) => handleOverflowClick(e, rowIndex)}
                >
                    <OverFlowIcon />
                </IconButton>
                {selectIndex === rowIndex ? ReactDOM.createPortal(
                    <div
                        className="tw-fixed tw-z-[9999] tw-w-48"
                        style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
                    >
                        <Menu
                            variant="compact"
                            items={menuItems}
                            data-testid={`list-listtable-overflow-menu-${rowKey}`}
                            onDismiss={() => setSelectIndex(null)}
                        />
                    </div>,
                    document.body
                ) : null}
            </>
        );
    };

    const columns: TableColumn<any>[] = [
        {
            id: 'name',
            header: 'Title',
            accessor: 'name',
            sortable: true,
            render: (_value, row, rowIndex) => (
                <div
                    role="button"
                    data-testid={`list-listtable-row-name-${row?.id ?? rowIndex}`}
                    /* accent-bold, not accent: resting accent is #030562, which next
                       to the cells' #172b4d ink reads as plain text, not a link.
                       accent-bold is the saturated brand blue the primary button
                       wears — 11.94:1 on white — so the column reads clickable. */
                    className="tw-text-accent-bold hover:tw-underline tw-cursor-pointer"
                    onClick={() => handleClick(rowIndex)}
                >
                    {row?.name ?? ''}
                </div>
            ),
        },
        { id: 'itemCount', header: 'Items', accessor: (r: any) => r?.itemCount ?? 0, sortable: true, cellType: 'numerical' },
        { id: 'created_at', header: 'Created On', accessor: (r: any) => formatDateDDMMMYYYY(r?.created_at) ?? '', sortable: true, cellType: 'meta' },
        { id: 'created_by', header: 'Created By', accessor: (r: any) => r?.created_by ?? '', sortable: true },
        { id: 'actions', header: '', accessor: () => null, width: '48px', align: 'right', render: (_v, row, rowIndex) => renderRowActions(row, rowIndex) },
    ];

    return (
        <>
            <div className="tw-mt-3">
                <Table
                    /* Bound the grid so <main> stops scrolling the page — see
                       `pageGridHeight.ts` for the measured reserve. `stickyHeader`
                       needs it: sticky resolves against a scrolling ancestor. */
                    stickyHeader
                    maxHeight={PAGE_GRID_MAX_HEIGHT}
                    columns={columns}
                    data={listData ?? []}
                    rowKey="id"
                    striped
                    hoverable
                    loading={loading}
                    emptyContent={emptyContent}
                    sortState={{ columnId: sortBy, direction: sortOrder === 'ASC' ? 'asc' : 'desc' }}
                    onSort={(columnId) => onSort(columnId)}
                    data-testid="lists-table"
                />
            </div>
            {/* 2026-09-16 — the rows-per-page control follows the pager's own rule (DS
                `Pagination` renders nothing for `totalItems <= 0`): a control over zero
                rows controls nothing, and it was standing alone under the empty state
                once the pager beside it had hidden itself. Guide gates its footer the
                same way; this was swept here from the Product Cards report. */}
            {totalRecords > 0 && (
                <div className="tw-mt-3 tw-mb-4 tw-flex tw-items-center tw-justify-end tw-gap-3">
                    <Dropdown
                        label=""
                        aria-label="Rows per page"
                        size="sm"
                        className="tw-w-[84px]"
                        options={PAGE_SIZE_OPTIONS}
                        value={paginationParams?.per_page}
                        onChange={(value) => updatePageSize(Number(value))}
                        data-testid="lists-page-size"
                    />
                    <Pagination
                        page={paginationParams?.page}
                        totalItems={totalRecords}
                        pageSize={paginationParams?.per_page}
                        onPageChange={handlePageChange}
                        size="sm"
                        data-testid="lists-pagination"
                    />
                </div>
            )}
        </>
    );
}

export default ListTable;
